/**
 * Pure rules for the destination-account identity check, kept out of the
 * component so they are testable without a DOM.
 */
import type { CallBrief, CallChannel, PendingCheck } from '../../services/bankAccountChecks.service';
import { readerLocale } from '../../i18n/language';

/** Mirrors the server floor so the button is disabled before the round-trip. */
export const MIN_NOTES = 10;
export const MIN_RELEASE_REASON = 20;

/**
 * What a queue row says about itself, before anybody opens it.
 *
 * Three states, not two. "Waiting for a call" on a row nobody is allowed to dial
 * is a queue that wastes the agent working it: they open it, read that they
 * cannot call, close it, and meet it again tomorrow at the top of the list —
 * oldest first — for as long as the wait lasts.
 *
 * A manager's row stays loud. A row that is merely waiting goes quiet: nothing
 * is wrong with it and nobody needs to do anything today.
 */
export function queueLabel(row: Pick<PendingCheck, 'verification_status' | 'channel'>): string {
  if (row.verification_status === 'escalated') return 'Needs a manager';
  switch (row.channel) {
    case 'phone_recently_changed':
      return 'Waiting on the number';
    case 'no_proven_phone':
      return 'No confirmed number';
    default:
      return 'Waiting for a call';
  }
}

export function queueTone(
  row: Pick<PendingCheck, 'verification_status' | 'channel'>,
): 'danger' | 'warning' | 'neutral' {
  if (row.verification_status === 'escalated') return 'danger';
  return row.channel === 'phone_recently_changed' ? 'neutral' : 'warning';
}

/**
 * Whether an agent may record a CONFIRMATION from this call.
 *
 * Only over a channel we trust. A number nobody proved, or one changed inside
 * the takeover window, cannot confirm anything — the server refuses it too, and
 * the screen must not invite an agent into a refusal.
 */
export function canConfirm(brief: Pick<CallBrief, 'channel'>): boolean {
  return brief.channel === 'ready';
}

/**
 * Why the call cannot happen yet — and, where there is one, WHEN it can.
 *
 * The date is the whole difference between a task an agent can park and a task
 * they think is stuck. A number changed in the same week the account was added
 * is not a second problem: it is the same event as the wait on the number, and
 * it becomes callable when that wait ends. Until 2026-09-24 this said "escalate
 * it to a manager", which turned an ordinary week — switching bank and phone
 * together, as anyone moving house does — into a manager's judgement call on a
 * client nobody had found anything wrong with.
 */
export function whyCannotConfirm(
  brief: Pick<CallBrief, 'channel' | 'callable_from'>,
): string | null {
  switch (brief.channel) {
    case 'ready':
      return null;
    case 'no_proven_phone':
      return 'This client has no confirmed phone number, so there is no channel we can '
        + 'trust. Ask them to confirm their number first.';
    case 'phone_recently_changed': {
      const when = formatCallableFrom(brief.callable_from);
      return 'This client changed their number and added this account within the same few '
        + 'days — one event, not two. A call now proves nothing, and nothing is wrong. '
        + (when === null
          ? 'It becomes callable when the wait on the number ends.'
          : `It becomes callable on ${when}.`)
        + ' Do not escalate it.';
    }
    default:
      return 'This client cannot be confirmed by phone right now.';
  }
}

/** An ISO date as a person would write it, or null rather than an invented one. */
export function formatCallableFrom(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(readerLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

/** A task that is merely WAITING, as against one that needs somebody. */
export function isWaitingForTheNumber(brief: Pick<CallBrief, 'channel'>): boolean {
  return brief.channel === 'phone_recently_changed';
}

/**
 * How far it was, in words, AGAINST THIS CLIENT'S OWN RANGE.
 *
 * The distance alone says nothing: 460 km is another world for someone who has
 * never left one city and a Tuesday for someone who rotates between three. So
 * the sentence carries both numbers whenever we know them, and the reviewer
 * judges the gap rather than the kilometre count.
 *
 * Never a coordinate — that is not a reviewer's business.
 */
export function describeDistance(
  km: number | null,
  usualRangeKm: number | null | undefined = undefined,
): string {
  if (km === null) return 'Registered from a place we could not compare';
  const from = `Registered about ${Math.round(km)} km from anywhere this client has used before`;
  if (usualRangeKm === null || usualRangeKm === undefined) return from;
  return `${from}, and they usually stay within about ${Math.round(usualRangeKm)} km`;
}

/** Oldest first: a queue is worked from the end that has waited longest. */
export function byOldestFirst(rows: PendingCheck[]): PendingCheck[] {
  return [...rows].sort((a, b) =>
    a.verification_opened_at.localeCompare(b.verification_opened_at));
}

export function isNoteReady(notes: string): boolean {
  return notes.trim().length >= MIN_NOTES;
}

export function isReleaseReady(reason: string): boolean {
  return reason.trim().length >= MIN_RELEASE_REASON;
}

/** A channel that cannot confirm is also one where no number should be shown. */
export function dialNumber(brief: CallBrief): string | null {
  return brief.channel === 'ready' ? brief.phone_on_file : null;
}

export function attemptsLeft(brief: Pick<CallBrief, 'attempts_made' | 'attempts_allowed'>): number {
  return Math.max(0, brief.attempts_allowed - brief.attempts_made);
}

export const CHANNEL_LABEL: Record<CallChannel, string> = {
  ready: 'Safe to call',
  no_proven_phone: 'No confirmed number',
  phone_recently_changed: 'Not callable yet',
};
